/**
 * The formats of the Story stack (designs `asset-redesign/b Story - new` and `e Feed post - new`).
 * A feed post is the Story stack in the frame of its shape: the same blocks, the same order, the
 * same theme frames, with its own size table and safe column (owner answer 2026-10-08: "the same
 * stack as the Story, scaled"). The square feed post is the 4:5 stack scaled to 0.8: it is laid
 * out on a 1350 x 1350 canvas and drawn at 1080 x 1080.
 */
export const STORY_FORMAT_IDS = ['story', 'feed45', 'feed11', 'lyric11', 'video916', 'moment916'] as const;
export type StoryFormatId = (typeof STORY_FORMAT_IDS)[number];

/** The sizes of the stack in px of the canvas of its format, before the factors of the theme. */
export interface StorySizes {
  kicker: number;
  heroDate: number;
  heroWord: number;
  /** The hero of an announce feed post ("New single"). */
  heroNew: number;
  lyric: number;
  number: number;
  dayLabel: number;
  title: number;
  rowTitle: number;
  artist: number;
  when: number;
  link: number;
  pillHeight: number;
  pillPaddingX: number;
  pillLinkGap: number;
  gap: number;
  countdownGap: number;
  announceGap: number;
  /** The gap between the title and the artist under a large cover. */
  idGap: number;
  /** The gap between the title and the artist beside the small cover. */
  rowTextGap: number;
  /** The gap between the small cover and its title. */
  rowGap: number;
  /** Cover ground and Type only: no cover card, so the type grows (design `studio-details` ImageFrame). */
  heroDateLed: number;
  heroWordLed: number;
  heroNewLed: number;
  titleLed: number;
  /** Type only: the lyric and the day number grow too. */
  lyricType: number;
  numberType: number;
  /** The text size of the CTA pill. Absent: the artist size (the Story and the feed post). */
  pillText?: number;
}

/** The base sizes of the Story (`StoryFrame.dc.html`). */
export const STORY_SIZES: Readonly<StorySizes> = {
  kicker: 42,
  heroDate: 190,
  heroWord: 150,
  heroNew: 136,
  lyric: 150,
  number: 600,
  dayLabel: 76,
  title: 104,
  rowTitle: 72,
  artist: 44,
  when: 44,
  link: 32,
  pillHeight: 112,
  pillPaddingX: 72,
  pillLinkGap: 22,
  gap: 34,
  countdownGap: 26,
  announceGap: 34,
  idGap: 12,
  rowTextGap: 10,
  rowGap: 34,
  heroDateLed: 280,
  heroWordLed: 230,
  heroNewLed: 200,
  titleLed: 150,
  lyricType: 175,
  numberType: 760,
};

/**
 * The base sizes of the 4:5 feed post (`FeedFrame.dc.html`, the build spec of page e): every size
 * about 12 % under the Story, so the stack fits a frame that is 570 px shorter.
 */
export const FEED_SIZES: Readonly<StorySizes> = {
  kicker: 40,
  heroDate: 160,
  heroWord: 130,
  heroNew: 120,
  lyric: 130,
  number: 520,
  dayLabel: 66,
  title: 84,
  rowTitle: 64,
  artist: 40,
  when: 40,
  link: 30,
  pillHeight: 100,
  pillPaddingX: 64,
  pillLinkGap: 16,
  gap: 26,
  countdownGap: 22,
  announceGap: 24,
  idGap: 10,
  rowTextGap: 8,
  rowGap: 30,
  heroDateLed: 236,
  heroWordLed: 196,
  heroNewLed: 176,
  titleLed: 128,
  lyricType: 150,
  numberType: 660,
};

/**
 * The sizes of the lyric card (`k Lyric card - new`, "Build spec for the Remotion layout"): every
 * size is the feed post size times about 0.8. The lyric grows to 216 px; 108 px (xl) is its full
 * size. Only the lyric moment uses this format: the other sizes are the feed post sizes times 0.8.
 */
export const LYRIC_CARD_SIZES: Readonly<StorySizes> = {
  kicker: 32,
  heroDate: 128,
  heroWord: 104,
  heroNew: 96,
  lyric: 216,
  number: 416,
  dayLabel: 52,
  title: 68,
  rowTitle: 56,
  artist: 30,
  when: 32,
  link: 26,
  pillHeight: 80,
  pillPaddingX: 52,
  pillLinkGap: 12,
  gap: 26,
  countdownGap: 18,
  announceGap: 20,
  idGap: 8,
  rowTextGap: 6,
  rowGap: 24,
  heroDateLed: 188,
  heroWordLed: 156,
  heroNewLed: 140,
  titleLed: 102,
  lyricType: 216,
  numberType: 528,
  pillText: 32,
};

/** The factor of a 9:16 short video against the Story (design `asset-redesign/o Countdown - new`, `--u`). */
export const VIDEO_STORY_UNIT = 0.88;

/**
 * The sizes of the Story stack in a 9:16 short video (the countdown, design o): every Story size
 * times 0.88, so the stack fits the video safe column (y 270 to 1440, clear of the Reels, TikTok and
 * Shorts captions and button rails). Pure data.
 */
export const VIDEO_SIZES: Readonly<StorySizes> = Object.fromEntries(
  Object.entries(STORY_SIZES).map(([key, value]) => [key, Math.round(value * VIDEO_STORY_UNIT * 100) / 100])
) as unknown as StorySizes;

/**
 * The sizes of the cover row and the CTA group of a lyric moment (design `asset-redesign/u`,
 * `LyricMomentFrame`): a 190 px cover, the title at 70 px, the pill 104 px high with 42 px text,
 * the SmartLink line at 30 px. The lyric itself is the lyric column of the video, not this stack.
 */
export const MOMENT_SIZES: Readonly<StorySizes> = {
  ...VIDEO_SIZES,
  rowTitle: 70,
  artist: 40,
  rowGap: 30,
  rowTextGap: 10,
  link: 30,
  pillHeight: 104,
  pillPaddingX: 64,
  pillLinkGap: 18,
  pillText: 42,
  countdownGap: 26,
};

/** The full size of the lyric of a lyric card (xl, owner default 6 of page k) and its floor (70 %). */
export const LYRIC_CARD_FULL_LYRIC_PX = 108;

export interface StoryFormat {
  id: StoryFormatId;
  /** The canvas of the layout. The render scales it to the width of the file. */
  size: { width: number; height: number };
  /** The safe column: no text, cover or logo above `safeTop` or under `safeBottom`. */
  safeTop: number;
  safeBottom: number;
  sizes: Readonly<StorySizes>;
  /**
   * `top`: the stack starts at the top of the safe column (a Story: its hero leads under the
   * covered band). `centre`: the items above the CTA are one group, centred in the free space
   * between the top of the safe column and the pill (the feed post).
   */
  stack: 'top' | 'centre';
  /** The factor of the frame details (borders, paddings, shadows, ornaments) against the Story. */
  unit: number;
  /** The cover sizes of the theme frame to read (`StoryFrame.cover` or `StoryFrame.feedCover`). */
  cover: 'story' | 'feed';
  /** The factor of the cover sizes of the theme frame (a short video: 0.88). Absent: 1. */
  coverScale?: number;
  /**
   * The room under `safeBottom` that the Free watermark may take (a lyric moment: the watermark goes
   * under the link line, down to y 1500, and the CTA stays in place). Absent: 0.
   */
  watermarkRoom?: number;
  /**
   * Room above the stack inside the safe column (a short video: 20 px, design o `.sc { top: 20px }`),
   * so the day number can grow up on the kick and stay inside the column. Absent: 0.
   */
  stackTopRoom?: number;
  /**
   * The logo height L of the Free watermark lockup (a stack, design w0 `story/watermark.ts`): 52 on
   * every 1080-wide file, 65 on the 1350 canvas of the square feed post (drawn at 0.8).
   */
  watermarkL: number;
  /** The side margin of the safe column for every theme. Absent: the margin of the theme frame. */
  marginX?: number;
  /** The small cover of the cover row. Absent: the small cover of the theme frame. */
  smallCover?: number;
  /**
   * The lyric of a lyric card: at most `maxLines` lines; the theme factors do not scale it (one full
   * size for every theme, owner default 6 of page k); it keeps the artist's own case (owner answer 3).
   * Absent: the lyric of a Story (5 lines, the theme factors, the theme case).
   */
  lyric?: { maxLines: number; themeFactors: boolean; keepCase: boolean };
}

/** The detail factor of the feed post: its sizes are about 12 % under the Story. */
const FEED_UNIT = 0.88;

export const STORY_FORMATS: Readonly<Record<StoryFormatId, StoryFormat>> = {
  // 1080 x 1920: the app covers the top 250 px and the bottom 340 px.
  story: {
    id: 'story',
    size: { width: 1080, height: 1920 },
    safeTop: 270,
    safeBottom: 1560,
    sizes: STORY_SIZES,
    stack: 'top',
    unit: 1,
    cover: 'story',
    watermarkL: 52,
  },
  // 1080 x 1350: no app covers a band; 72 px at the top and the bottom hold only the ground.
  feed45: {
    id: 'feed45',
    size: { width: 1080, height: 1350 },
    safeTop: 72,
    safeBottom: 1278,
    sizes: FEED_SIZES,
    stack: 'centre',
    unit: FEED_UNIT,
    cover: 'feed',
    watermarkL: 52,
  },
  // 1080 x 1080, drawn from a 1350 x 1350 canvas: the 4:5 stack at 0.8 (owner answer 2026-10-08).
  feed11: {
    id: 'feed11',
    size: { width: 1350, height: 1350 },
    safeTop: 72,
    safeBottom: 1278,
    sizes: FEED_SIZES,
    stack: 'centre',
    unit: FEED_UNIT,
    cover: 'feed',
    watermarkL: 65,
  },
  // 1080 x 1080, the lyric card (design k): the safe column x 150 to 930 (inside the 3:4 grid crop),
  // y 64 to 1016; the stack is centred; the frame details are the Story values times 0.6.
  lyric11: {
    id: 'lyric11',
    size: { width: 1080, height: 1080 },
    safeTop: 64,
    safeBottom: 1016,
    sizes: LYRIC_CARD_SIZES,
    stack: 'centre',
    unit: 0.6,
    cover: 'feed',
    marginX: 150,
    smallCover: 120,
    lyric: { maxLines: 4, themeFactors: false, keepCase: true },
    watermarkL: 52,
  },
  // 1080 x 1920, a 9:16 short video (the countdown, design o): the Story stack at 0.88 in the video
  // safe column x 140 to 940, y 270 to 1440. The Reels, TikTok and Shorts captions cover the frame
  // from y 1480, their button rails x 940 to 1080 (owner answer 2026-10-08).
  video916: {
    id: 'video916',
    size: { width: 1080, height: 1920 },
    safeTop: 270,
    safeBottom: 1440,
    sizes: VIDEO_SIZES,
    stack: 'top',
    unit: VIDEO_STORY_UNIT,
    cover: 'story',
    coverScale: VIDEO_STORY_UNIT,
    marginX: 140,
    stackTopRoom: 20,
    watermarkL: 52,
  },
  // 1080 x 1920, the cover row and the CTA group of a lyric moment (design u): the row is centred in
  // y 1040 to the pill (about y 1050 to 1250), the pill starts near y 1290, the link line ends at
  // y 1450; the Free watermark lockup goes under it, down to y 1500 (the CTA group then ends at y 1350). The lyric column is above (y 300 to 1000).
  moment916: {
    id: 'moment916',
    size: { width: 1080, height: 1920 },
    safeTop: 1040,
    safeBottom: 1450,
    sizes: MOMENT_SIZES,
    stack: 'centre',
    unit: 0.7,
    cover: 'story',
    marginX: 140,
    smallCover: 190,
    watermarkRoom: 50,
    watermarkL: 52,
  },
};

export function isStoryFormatId(value: unknown): value is StoryFormatId {
  return typeof value === 'string' && (STORY_FORMAT_IDS as readonly string[]).includes(value);
}

/** The format of a format id; absent or unknown: the Story. */
export function storyFormatOf(id: string | null | undefined): StoryFormat {
  return isStoryFormatId(id) ? STORY_FORMATS[id] : STORY_FORMATS.story;
}

/** The Story format of an asset spec id (`story916`, `feed45`, `feed11`); null for every other spec. */
export function storyFormatIdOfSpec(specId: string): StoryFormatId | null {
  switch (specId) {
    case 'story916':
      return 'story';
    case 'feed45':
      return 'feed45';
    case 'feed11':
      return 'feed11';
    case 'lyricCard11':
      return 'lyric11';
    default:
      return null;
  }
}

/** True for a feed post format (4:5 or 1:1). */
export function isFeedFormat(id: StoryFormatId | null | undefined): boolean {
  return id === 'feed45' || id === 'feed11';
}

/**
 * True for a format whose SmartLink line starts with "Link in bio": the feed post and the lyric card
 * (owner answers 2026-10-08: a link in a feed image cannot be tapped).
 */
export function hasLinkNote(id: StoryFormatId | null | undefined): boolean {
  return isFeedFormat(id) || id === 'lyric11';
}
